import { Fragment } from 'react';
import type { CheckboxGroupProps as ReactAriaCheckboxGroupProps } from 'react-aria-components';
import { CheckboxGroup as AriaCheckboxGroup } from 'react-aria-components';

import type { FieldProps } from '../../../types';
import { mergeClassNames } from '../../../utils/mergeClassNames';
import { VisuallyHidden } from '../../display/VisuallyHidden';
import { Description } from '../Description';
import { ErrorMessage } from '../ErrorMessage';
import { Label } from '../Label';
import styles from './CheckboxGroup.module.scss';

export type CheckboxGroupProps = ReactAriaCheckboxGroupProps & FieldProps;

/**
 * `CheckboxGroup` groups a number of `Checkbox` components, allowing them to be treated as one form element.
 *
 * ```ts
 * <CheckboxGroup
 *   helpText="Description"
 *   errorMessage="Errored"
 *   label="Checkbox group label"
 *   defaultValue={['option1', 'option3']}
 * >
 *   <Checkbox value="option1">Option 1</Checkbox>
 *   <Checkbox value="option2">Option 2</Checkbox>
 *   <Checkbox value="option3">Option 3</Checkbox>
 *   <Checkbox value="option4">Option 4</Checkbox>
 * </CheckboxGroup>
 * ```
 */
export const CheckboxGroup = ({
  children,
  className,
  label,
  helpText,
  errorMessage,
  isLabelHidden,
  isRequired,
  ...props
}: CheckboxGroupProps) => {
  const LabelWrapper = isLabelHidden ? VisuallyHidden : Fragment;

  return (
    <AriaCheckboxGroup
      className={(renderProps) =>
        mergeClassNames([styles.checkboxGroup, className], renderProps)
      }
      data-zds
      {...props}
    >
      {(checkboxGroupRenderProps) => (
        <>
          {label && (
            <LabelWrapper>
              <Label isRequired={isRequired}>{label}</Label>
            </LabelWrapper>
          )}
          <div className={styles.optionsWrapper}>
            {typeof children === 'function'
              ? children(checkboxGroupRenderProps)
              : children}
          </div>
          {helpText && <Description>{helpText}</Description>}
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </>
      )}
    </AriaCheckboxGroup>
  );
};
